/**
 * Image Upload Utility
 * Uses backend server-side Cloudinary upload to avoid needing frontend env vars
 */

const API_URL = '';

/**
 * Upload an image via the backend Cloudinary endpoint using XMLHttpRequest
 * This avoids issues with fetch response body being consumed by interceptors
 * @param {File} file - The file to upload
 * @param {Object} options - Optional settings
 * @returns {Promise<{ok: boolean, url?: string, error?: string}>}
 */
export function uploadImage(file, options = {}) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);
    
    xhr.open('POST', `${API_URL}/api/upload/image`, true);
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({
            ok: true,
            url: data.url,
            public_id: data.public_id
          });
        } catch (e) {
          console.error('Failed to parse success response:', e);
          resolve({
            ok: false,
            error: 'Invalid response from server'
          });
        }
      } else {
        // Error response
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({
            ok: false,
            error: data.detail || data.error || data.message || `Upload failed (${xhr.status})`
          });
        } catch (e) {
          resolve({
            ok: false,
            error: xhr.responseText || `Upload failed with status ${xhr.status}`
          });
        }
      }
    };
    
    xhr.onerror = function() {
      console.error('XHR network error');
      resolve({
        ok: false,
        error: 'Network error during upload'
      });
    };
    
    xhr.ontimeout = function() {
      resolve({
        ok: false,
        error: 'Upload timed out'
      });
    };
    
    // Set timeout to 60 seconds for large files
    xhr.timeout = 60000;
    
    xhr.send(formData);
  });
}

/**
 * Check if image uploads are enabled
 * @returns {Promise<{enabled: boolean, configured: boolean}>}
 */
export async function checkUploadStatus() {
  try {
    const response = await fetch(`${API_URL}/api/config-status`);
    
    if (!response.ok) {
      return { enabled: false, configured: false };
    }
    
    const data = await response.json();
    
    return {
      enabled: !data.image_upload_disabled,
      configured: data.cloudinary_configured
    };
  } catch (error) {
    console.error('Failed to check upload status:', error);
    return {
      enabled: false,
      configured: false
    };
  }
}
